import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'

// ── Context ────────────────────────────────────────────────────────────────────

export interface SettingsDrawerContextValue {
  isOpen: boolean
  open: () => void
  close: () => void
}

export const SettingsDrawerContext =
  createContext<SettingsDrawerContextValue | null>(null)

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * Access the global SettingsDrawer open/close controls.
 *
 * Must be called inside a component tree that has <SettingsDrawer /> mounted
 * (App.tsx root). TopBar cog (commit 8) calls `open()` here.
 */
export function useSettingsDrawer(): SettingsDrawerContextValue {
  const ctx = useContext(SettingsDrawerContext)
  if (ctx === null) {
    throw new Error(
      'useSettingsDrawer must be used within a component tree that mounts <SettingsDrawer />',
    )
  }
  return ctx
}

// ── State factory (used internally by SettingsDrawerProvider) ────────────────

export function useSettingsDrawerState(): SettingsDrawerContextValue {
  const [isOpen, setIsOpen] = useState(false)

  const open = useCallback((): void => {
    setIsOpen(true)
  }, [])

  const close = useCallback((): void => {
    setIsOpen(false)
  }, [])

  return { isOpen, open, close }
}

// ── Provider — must wrap the consumer subtree (AppShell, routes) ──────────────

/**
 * Wrap the part of the tree that needs to call useSettingsDrawer().
 * The drawer body itself (<SettingsDrawer />) is rendered as a sibling INSIDE
 * this provider so both producer (body) and consumers (TopBar cog via
 * AppShell) share the same context instance.
 */
export function SettingsDrawerProvider({
  children,
}: {
  children: ReactNode
}): ReactElement {
  const ctx = useSettingsDrawerState()
  return (
    <SettingsDrawerContext.Provider value={ctx}>
      {children}
    </SettingsDrawerContext.Provider>
  )
}

// Re-export ReactNode so callers don't need a separate import.
export type { ReactNode }
